import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'sonner'
import { Loader2, X } from 'lucide-react'
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useDrawerDispatch } from '@/context/DrawerContext'
import { useConfirmation } from '@/context/ConfirmationContext'
import SofaFormTypeGridSelect from '@/components/shared/inputComponents/SofaFormTypeGridSelect'
import { BlueprintDimensionsEditor } from '@/containers/Products/components/SofaDimensionsBlueprintField'
import { loadSofaElement } from '@/configurator/DynamicLoadSofaShape'
import {
  getShapeChangeableProperties,
  type ChangeableProperties,
} from '@/configurator/utils/shapeConfigUtils'
import type { BlueprintDimKey } from '@/configurator/blueprint-overlay/types'
import {
  DELETE_ERP_SHAPE_MAPPING,
  ERP_SHAPE_MAPPING_REFETCH,
  GET_ERP_SHAPE_MAPPING,
  UPLOAD_ERP_SHAPE_MAPPING_IMAGE,
  UPSERT_ERP_SHAPE_MAPPING,
} from '../erpShapeMappingQueries'
import {
  MAPPING_PARAM_KEYS,
  PARAM_KEY_ORDER,
  PARAM_LABELS,
  cleanParams,
  isOnBlueprintParam,
  isParamAllowed,
  parseParams,
  variantToParams,
  type MappingParamKey,
  type MappingParams,
} from '../params'
import { ErpImagePanel } from './ErpImagePanel'
import { NonStandardShapeNotice } from '@/components/shared/NonStandardShapeBadge'
import { isFeaturedSofaType } from '@/configurator/shapeConfig'
import { errorMessage, type ErpShapeMappingRow } from '../types'
import { STATUS_BADGE } from './columns'

const STATUSES = ['MAPPED', 'UNMAPPED', 'IGNORED'] as const

const formSchema = z.object({
  erp_schematic_id: z
    .string()
    .trim()
    .min(1, 'Schematic ID is required')
    .max(100, 'Max 100 characters'),
  status: z.enum(STATUSES),
  sofa_form_type: z.string().nullable(),
  notes: z.string().optional(),
  params: z.record(z.any()),
})

type FormValues = z.infer<typeof formSchema>

interface ErpShapeMappingSheetProps {
  /** A table row (edit) or `{ manufacturerId }` (create). */
  data?: Partial<ErpShapeMappingRow>
  onRequestClose?: () => void
  onDirtyChange?: (dirty: boolean) => void
}

// Preview-only sizes: the real width/length come from each ERP item.
type SampleDims = { width: number | null; length: number | null }

export default function ErpShapeMappingSheet({
  data,
  onDirtyChange,
}: ErpShapeMappingSheetProps) {
  const dispatch = useDrawerDispatch()
  const { showConfirmation } = useConfirmation()
  const mappingId: number | undefined =
    typeof data?.id === 'number' ? data.id : undefined
  const isEdit = mappingId !== undefined

  const { data: queryData } = useQuery(GET_ERP_SHAPE_MAPPING, {
    variables: { id: mappingId },
    skip: !isEdit,
    fetchPolicy: 'network-only',
  })
  const mapping: Partial<ErpShapeMappingRow> | null =
    queryData?.erpShapeMapping ?? (isEdit ? (data ?? null) : null)
  const manufacturerId: number | undefined =
    mapping?.manufacturerId ?? data?.manufacturerId

  const [upsertMapping, { loading: saving }] = useMutation(
    UPSERT_ERP_SHAPE_MAPPING
  )
  const [uploadImage] = useMutation(UPLOAD_ERP_SHAPE_MAPPING_IMAGE)
  const [deleteMapping] = useMutation(DELETE_ERP_SHAPE_MAPPING, {
    refetchQueries: ERP_SHAPE_MAPPING_REFETCH,
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      erp_schematic_id: '',
      status: 'UNMAPPED',
      sofa_form_type: null,
      notes: '',
      params: {},
    },
  })

  const [savedImage, setSavedImage] = useState<{
    url: string | null
    thumb: string | null
  }>({ url: null, thumb: null })

  // Seed the form once the row (or its fresh copy) is available.
  const seededFor = React.useRef<string | null>(null)
  useEffect(() => {
    if (!mapping) return
    const key = `${mapping.id}:${mapping.updatedAt}`
    if (seededFor.current === key) return
    seededFor.current = key
    form.reset({
      erp_schematic_id: mapping.erp_schematic_id ?? '',
      status: mapping.status ?? 'UNMAPPED',
      sofa_form_type: mapping.sofa_form_type ?? null,
      notes: mapping.notes ?? '',
      params: parseParams(mapping.default_parameters),
    })
    setSavedImage({
      url: mapping.erp_image_url ?? null,
      thumb: mapping.erp_image_thumb_url ?? null,
    })
  }, [mapping, form])

  const type = useWatch({ control: form.control, name: 'sofa_form_type' })
  const params = (useWatch({ control: form.control, name: 'params' }) ??
    {}) as MappingParams
  const status = useWatch({ control: form.control, name: 'status' })

  // ---- Image ---------------------------------------------------------------
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [pendingPreview, setPendingPreview] = useState<string | null>(null)
  const [clearImage, setClearImage] = useState(false)
  const [uploading, setUploading] = useState(false)

  useEffect(
    () => () => {
      if (pendingPreview) URL.revokeObjectURL(pendingPreview)
    },
    [pendingPreview]
  )

  const uploadTo = useCallback(
    async (id: number, file: File) => {
      setUploading(true)
      try {
        const { data: res } = await uploadImage({
          variables: { mappingId: id, file },
          refetchQueries: ERP_SHAPE_MAPPING_REFETCH,
        })
        const updated = res?.uploadErpShapeMappingImage
        setSavedImage({
          url: updated?.erp_image_url ?? null,
          thumb: updated?.erp_image_thumb_url ?? null,
        })
        return true
      } catch (error) {
        console.error('ERP image upload failed:', error)
        toast.error(errorMessage(error, 'Image upload failed'))
        return false
      } finally {
        setUploading(false)
      }
    },
    [uploadImage]
  )

  const handleFile = useCallback(
    async (file: File) => {
      setClearImage(false)
      if (mappingId !== undefined) {
        // Existing row: upload straight away so pasting many images in a
        // row doesn't need a save each time.
        if (await uploadTo(mappingId, file)) toast.success('ERP image saved')
        return
      }
      setPendingFile(file)
      setPendingPreview(URL.createObjectURL(file))
    },
    [mappingId, uploadTo]
  )

  const handleRemoveImage = () => {
    if (pendingFile) {
      setPendingFile(null)
      setPendingPreview(null)
      return
    }
    setClearImage(true)
  }

  // ---- Shape type + changeable properties ----------------------------------
  const [changeable, setChangeable] = useState<ChangeableProperties | null>(
    null
  )
  const [propertiesType, setPropertiesType] = useState<string | null>(null)
  const [sample, setSample] = useState<SampleDims>({
    width: null,
    length: null,
  })
  const [showAllParams, setShowAllParams] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!type) {
      setChangeable(null)
      setPropertiesType(null)
      return
    }
    Promise.all([getShapeChangeableProperties(type), loadSofaElement(type)])
      .then(([props, mod]) => {
        if (cancelled) return
        setChangeable(props)
        setPropertiesType(type)
        const defaults = mod?.getDefaultSettings?.().dimensions
        setSample({
          width: defaults?.width ?? 100,
          length: defaults?.length ?? 100,
        })
      })
      .catch(() => {
        if (cancelled) return
        setChangeable(null)
        setPropertiesType(type)
      })
    return () => {
      cancelled = true
    }
  }, [type])

  const handleTypeSelect = (
    typeName: string,
    variant?: Record<string, number | string | boolean>
  ) => {
    form.setValue('sofa_form_type', typeName, { shouldDirty: true })
    if (variant) {
      form.setValue(
        'params',
        { ...form.getValues('params'), ...variantToParams(variant) },
        { shouldDirty: true }
      )
    }
    if (form.getValues('status') === 'UNMAPPED') {
      form.setValue('status', 'MAPPED', { shouldDirty: true })
    }
  }

  const clearType = () => {
    form.setValue('sofa_form_type', null, { shouldDirty: true })
    if (form.getValues('status') === 'MAPPED') {
      form.setValue('status', 'UNMAPPED', { shouldDirty: true })
    }
  }

  const setParam = (key: MappingParamKey, value: unknown) => {
    const next = { ...form.getValues('params') }
    if (value === null || value === undefined || value === '') delete next[key]
    else next[key] = value
    form.setValue('params', next, { shouldDirty: true })
  }

  // ---- Blueprint editor wiring ----------------------------------------------
  const blueprintChangeable: ChangeableProperties = useMemo(
    () => ({
      ...(changeable ?? {}),
      // Not a mapping parameter (the ERP import doesn't take it from here).
      corner_part_length: false,
    }),
    [changeable]
  )
  const blueprintParamKeys = PARAM_KEY_ORDER.filter(
    key => isOnBlueprintParam(key) && isParamAllowed(key, changeable)
  )
  const numericParam = (key: MappingParamKey) =>
    typeof params[key] === 'number' ? (params[key] as number) : null

  const blueprintValues: Partial<Record<BlueprintDimKey, number | null>> = {
    width: sample.width,
    length: sample.length,
    backrest_width: numericParam('backrest_width'),
    armrest_width: numericParam('armrest_width'),
    extendable_part_length: numericParam('extendable_part_length'),
    corner_radius: numericParam('corner_radius'),
    angle: numericParam('angle'),
  }

  const commitBlueprint = (key: BlueprintDimKey, value: number | null) => {
    if (key === 'width' || key === 'length') {
      setSample(prev => ({ ...prev, [key]: value }))
      return
    }
    if (isOnBlueprintParam(key)) setParam(key, value)
  }

  const plainParamKeys = PARAM_KEY_ORDER.filter(key => {
    if (blueprintParamKeys.includes(key)) return false
    return (
      showAllParams ||
      isParamAllowed(key, changeable) ||
      params[key] !== undefined
    )
  })

  // ---- Dirty tracking --------------------------------------------------------
  const { isDirty } = form.formState
  useEffect(() => {
    onDirtyChange?.(isDirty || !!pendingFile || clearImage)
  }, [isDirty, pendingFile, clearImage, onDirtyChange])

  const close = () => {
    onDirtyChange?.(false)
    dispatch({ type: 'CLOSE_DRAWER' })
  }

  // ---- Save / delete ---------------------------------------------------------
  const save = async (values: FormValues) => {
    if (!manufacturerId) {
      toast.error('No manufacturer selected')
      return
    }
    try {
      const { data: res } = await upsertMapping({
        variables: {
          input: {
            ...(isEdit ? { id: mappingId } : {}),
            manufacturerId,
            erp_schematic_id: values.erp_schematic_id.trim(),
            sofa_form_type: values.sofa_form_type || null,
            default_parameters: cleanParams(values.params as MappingParams),
            status: values.status,
            notes: values.notes?.trim() || null,
            ...(clearImage && !pendingFile ? { clear_image: true } : {}),
          },
        },
        refetchQueries: ERP_SHAPE_MAPPING_REFETCH,
      })
      const saved = res?.upsertErpShapeMapping
      if (pendingFile && saved?.id) {
        const ok = await uploadTo(saved.id, pendingFile)
        if (!ok) {
          toast.warning('Mapping saved, but the image upload failed')
          return
        }
      }
      toast.success(isEdit ? 'Mapping saved' : 'Mapping created')
      close()
    } catch (error) {
      console.error('Saving ERP shape mapping failed:', error)
      toast.error(errorMessage(error, 'Failed to save mapping'))
    }
  }

  const onSubmit = (values: FormValues) => {
    const originalType = mapping?.sofa_form_type ?? null
    const typeChanged =
      isEdit &&
      mapping?.status === 'MAPPED' &&
      !!originalType &&
      (values.sofa_form_type || null) !== originalType
    if (!typeChanged) {
      void save(values)
      return
    }
    const items =
      typeof mapping?.erp_item_count === 'number'
        ? ` ${mapping.erp_item_count} ERP item(s) currently use this schematic.`
        : ''
    showConfirmation({
      title: 'Change the mapped shape type?',
      description: `The next ERP import will change the type of every existing module with schematic ${mapping?.erp_schematic_id} from ${originalType} to ${values.sofa_form_type || '(none — those modules will be skipped)'}.${items}`,
      confirmText: 'Save anyway',
      cancelText: 'Cancel',
      onConfirm: () => void save(values),
    })
  }

  const onDelete = () => {
    if (!mappingId) return
    showConfirmation({
      title: 'Delete mapping?',
      description: `Delete the mapping for ${mapping?.erp_schematic_id}? ERP items with this schematic will be skipped by the import as "no shape" until it is mapped again.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await deleteMapping({ variables: { id: mappingId } })
          toast.success('Mapping deleted')
          close()
        } catch (error) {
          toast.error(errorMessage(error, 'Failed to delete mapping'))
        }
      },
    })
  }

  const imageUrl = clearImage ? null : savedImage.url

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <SheetTitle>
          {isEdit ? (
            <span className="font-mono">{mapping?.erp_schematic_id}</span>
          ) : (
            'New ERP shape mapping'
          )}
        </SheetTitle>
        <SheetDescription>
          {isEdit
            ? `Id: ${mappingId}${mapping?.erp_sample_items?.length ? ` | e.g. ${mapping.erp_sample_items.slice(0, 3).join(', ')}` : ''}`
            : 'Map an ERP module schematic to one of our sofa shapes'}
        </SheetDescription>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mx-auto max-w-3xl space-y-6 py-5"
          >
            <section className="space-y-2">
              <h3 className="text-sm font-medium">ERP schematic image</h3>
              <ErpImagePanel
                imageUrl={imageUrl}
                pendingPreviewUrl={pendingPreview}
                uploading={uploading}
                onFile={handleFile}
                onRemove={handleRemoveImage}
              />
              {clearImage && (
                <p className="text-xs text-amber-700">
                  The image will be removed when you save.
                </p>
              )}
            </section>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="erp_schematic_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Schematic ID</FormLabel>
                    <FormControl>
                      <Input
                        className="font-mono"
                        placeholder="e.g. B-X3-2SP-D"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {STATUSES.map(s => (
                          <SelectItem
                            key={s}
                            value={s}
                            disabled={s === 'MAPPED' && !type}
                          >
                            {STATUS_BADGE[s].label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Our shape</h3>
                {type && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearType}
                  >
                    <X className="mr-1 h-4 w-4" />
                    Clear type
                  </Button>
                )}
              </div>
              <SofaFormTypeGridSelect
                field={{
                  value: type ?? '',
                  onChange: (value: string) => handleTypeSelect(value),
                }}
                onSelect={handleTypeSelect}
                selectableVariants
              />
              {type && !isFeaturedSofaType(type) && (
                <NonStandardShapeNotice type={type} />
              )}
              <p className="text-xs text-muted-foreground">
                Tip: in the picker, click one of a family's variant pictures to
                also copy its parameters.
              </p>
            </section>

            {type && type !== 'COMPOSITE' && (
              <section className="space-y-2">
                <h3 className="text-sm font-medium">Blueprint</h3>
                <BlueprintDimensionsEditor
                  id={mappingId ?? 0}
                  type={type}
                  dimensions={{
                    ...(params as Record<string, number | string | boolean>),
                    width: sample.width,
                    length: sample.length,
                  }}
                  values={blueprintValues}
                  onCommit={commitBlueprint}
                  changeableProperties={blueprintChangeable}
                  propertiesType={propertiesType}
                  height={380}
                />
                <p className="text-xs text-muted-foreground">
                  Width and length here are only a preview size — the import
                  takes them from each ERP item. Backrest, armrest, extension,
                  radius and angle are saved with the mapping.
                </p>
              </section>
            )}
            {type === 'COMPOSITE' && (
              <p className="text-sm text-muted-foreground">
                Composite modules have no blueprint preview here.
              </p>
            )}

            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Parameters</h3>
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Switch
                    checked={showAllParams}
                    onCheckedChange={setShowAllParams}
                  />
                  Show all parameters
                </label>
              </div>
              {plainParamKeys.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {type
                    ? 'No other parameters for this shape.'
                    : 'Pick a shape first.'}
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {plainParamKeys.map(key => (
                    <ParamInput
                      key={key}
                      paramKey={key}
                      value={params[key]}
                      onChange={value => setParam(key, value)}
                    />
                  ))}
                </div>
              )}
            </section>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            <div className="flex items-center justify-between">
              {isEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive"
                  onClick={onDelete}
                >
                  Delete
                </Button>
              ) : (
                <span />
              )}
              <Button type="submit" disabled={saving || uploading}>
                {(saving || uploading) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEdit ? 'Save changes' : 'Create mapping'}
              </Button>
            </div>
            {status === 'IGNORED' && (
              <p className="text-xs text-muted-foreground">
                Ignored schematics stay out of the "Unmapped" list.
              </p>
            )}
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}

function ParamInput({
  paramKey,
  value,
  onChange,
}: {
  paramKey: MappingParamKey
  value: unknown
  onChange: (value: unknown) => void
}) {
  const spec = MAPPING_PARAM_KEYS[paramKey]
  const label = PARAM_LABELS[paramKey]
  const NONE = '__none__'

  if (spec.kind === 'enum' || spec.kind === 'boolean') {
    const options =
      spec.kind === 'enum'
        ? spec.values.map(v => ({ value: v, label: v }))
        : [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]
    const current =
      value === undefined || value === null ? NONE : String(value)
    return (
      <label className="space-y-1 text-sm">
        <span className="block text-xs font-medium">{label}</span>
        <Select
          value={current}
          onValueChange={v =>
            onChange(
              v === NONE
                ? null
                : spec.kind === 'boolean'
                  ? v === 'true'
                  : v
            )
          }
        >
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>— not set —</SelectItem>
            {options.map(o => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
    )
  }

  const unit = spec.kind === 'cm' ? 'cm' : spec.kind === 'angle' ? '°' : ''
  return (
    <label className="space-y-1 text-sm">
      <span className="block text-xs font-medium">{label}</span>
      <div className="relative">
        <Input
          type="number"
          className="h-9 pr-9"
          min={0}
          step={spec.kind === 'count' ? 1 : 'any'}
          value={typeof value === 'number' ? value : ''}
          placeholder="not set"
          onChange={e => {
            const raw = e.target.value
            onChange(raw === '' ? null : Number(raw))
          }}
        />
        {unit && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            {unit}
          </span>
        )}
      </div>
    </label>
  )
}
